import {call, put, take, takeLatest} from "redux-saga/effects";

import * as AuthActions from "@atg-shared/auth/domain/authActions";
import {TeamInviteUtils} from "@atg-tillsammans/team-utils";
import {ToastContainerId} from "@atg-tillsammans/toasts-types";
import {TeamInviteStatus} from "@atg-tillsammans/types";
import {Paths} from "@atg-tillsammans-shared/navigation";
import {ImageUtils} from "@atg-ui/avatar";
import history from "atg-history";
import * as ToastActions from "atg-ui-toast/domain/toastActions";

import {TeamActions} from "../team";

import * as InviteActions from "./invite.actions";
import * as InviteApi from "./inviteApi";

import type {SagaIterator} from "redux-saga";

export function* acceptInvite(
    action: InviteActions.AcceptInviteAction,
): SagaIterator<void> {
    yield put(AuthActions.checkAuth(true));
    const authResult = yield take(AuthActions.AUTH_CHECK_RESPONSE);
    if (authResult.error) return;

    const {teamId, inviteCode, gameId} = action.payload;

    try {
        const {status, team} = yield call(InviteApi.joinTeamByInvite, teamId, inviteCode);

        switch (status) {
            case TeamInviteStatus.JOINED:
            case TeamInviteStatus.ALREADY_MEMBER:
                if (status === TeamInviteStatus.JOINED) {
                    yield put(TeamActions.memberJoinedTeam({team}));
                }

                yield call(
                    history.push,
                    gameId
                        ? Paths.getTeamGamePagePath(teamId, gameId)
                        : Paths.getTeamPagePath(teamId),
                );
                if (status === TeamInviteStatus.JOINED)
                    yield put(
                        ToastActions.showToast({
                            containerId: ToastContainerId.TEAM_PAGE,
                            type: "success",
                            message: TeamInviteUtils.getMessages(status),
                            timeout: 10000,
                            reason: status,
                        }),
                    );
                break;

            case TeamInviteStatus.QUEUED:
            case TeamInviteStatus.ALREADY_QUEUED:
            case TeamInviteStatus.TO_MANY_TEAMS:
                yield put(InviteActions.acceptInviteResponse(status));
                break;
            case TeamInviteStatus.INVALID_INVITE:
            case TeamInviteStatus.TECHNICAL_ERROR:
            default:
                yield put(
                    InviteActions.acceptInviteError(
                        TeamInviteUtils.parseTeamInviteStatus(status),
                    ),
                );
                break;
        }
    } catch (error: unknown) {
        yield put(InviteActions.acceptInviteError(TeamInviteStatus.TECHNICAL_ERROR));
    }
}

export function* fetchInviteInfo({
    payload,
}: InviteActions.FetchInviteInfoAction): SagaIterator<void> {
    const {inviteCode, userName} = payload;
    try {
        const inviteInfoResponse = yield call(
            InviteApi.fetchInviteInfo,
            inviteCode,
            userName,
        );
        if (!inviteInfoResponse || inviteInfoResponse.meta.code === 404) {
            yield put(InviteActions.inviteErrorResponse(inviteInfoResponse.data));
        } else {
            const inviter = {
                ...inviteInfoResponse.data.inviter,
            };

            if (inviter.imageUrl) {
                inviter.imageUrl = ImageUtils.getImageRef(inviter.imageUrl);
            }

            yield put(
                InviteActions.inviteInfoResponse({
                    ...inviteInfoResponse.data,
                    inviter,
                }),
            );
        }
    } catch (error: unknown) {
        yield put(
            InviteActions.inviteErrorResponse({
                // @ts-expect-error
                message: error.response.data.message,
                // @ts-expect-error
                ...error,
            }),
        );
    }
}

export default function* InviteSaga(): SagaIterator<void> {
    yield takeLatest(InviteActions.FETCH_INVITE_INFO_REQUEST, fetchInviteInfo);
    yield takeLatest(InviteActions.ACCEPT_INVITE_ACTION, acceptInvite);
}
